import { useEffect } from "react"

export interface Props {
    rootId: string
}

const MAX_VISIBLE_THUMBS = 6

const setup = ({ rootId }: Props) => {
    const root = document.getElementById(rootId)
    if (!root) return

    const thumbnails = root.querySelector('[data-thumbnails]') as HTMLElement | null
    const prevArrows = root.querySelectorAll('[data-thumb-arrow="prev"]')
    const nextArrows = root.querySelectorAll('[data-thumb-arrow="next"]')
    const mainPrev = root.querySelector('[data-slide="prev"]') as HTMLButtonElement | null
    const mainNext = root.querySelector('[data-slide="next"]') as HTMLButtonElement | null
    const dots = root.querySelectorAll('[data-dot]')

    if (!thumbnails) return

    const isDesktop = () => globalThis.matchMedia('(min-width: 640px)').matches

    const hasOverflow = () => {
        if (!isDesktop()) return false
        if (dots.length <= MAX_VISIBLE_THUMBS) return false

        return thumbnails.scrollHeight > thumbnails.clientHeight + 1
    }

    const getActiveIndex = () => {
        const activeDot = root.querySelector('[data-dot][disabled]')
        return activeDot ? Number(activeDot.getAttribute('data-dot')) : 0
    }

    const updateButtons = () => {
        const overflow = hasOverflow()
        const activeIndex = getActiveIndex()
        const lastIndex = dots.length - 1

        prevArrows.forEach((arrow) => {
            arrow.classList.toggle('invisible', !overflow)
            arrow.classList.toggle('pointer-events-none', !overflow)

            const button = arrow.querySelector('button')
            if (button && overflow) {
                button.toggleAttribute('disabled', activeIndex <= 0)
            }
        })

        nextArrows.forEach((arrow) => {
            arrow.classList.toggle('invisible', !overflow)
            arrow.classList.toggle('pointer-events-none', !overflow)

            const button = arrow.querySelector('button')
            if (button && overflow) {
                button.toggleAttribute('disabled', activeIndex >= lastIndex)
            }
        })
    }

    const scrollActiveIntoView = () => {
        if (!isDesktop()) return

        const activeDot = root.querySelector('[data-dot][disabled]')
        const activeItem = activeDot?.closest('li')

        if (!activeItem) return

        activeItem.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth',
        })
    }

    const onPrevClick = () => {
        if (mainPrev && !mainPrev.disabled) {
            mainPrev.click()
        }
    }

    const onNextClick = () => {
        if (mainNext && !mainNext.disabled) {
            mainNext.click()
        }
    }

    prevArrows.forEach((arrow) => {
        arrow.querySelector('button')?.addEventListener('click', onPrevClick)
    })
    nextArrows.forEach((arrow) => {
        arrow.querySelector('button')?.addEventListener('click', onNextClick)
    })

    const dotObserver = new MutationObserver(() => {
        updateButtons()
        scrollActiveIntoView()
    })

    dots.forEach((dot) =>
        dotObserver.observe(dot, { attributes: true, attributeFilter: ['disabled'] })
    )

    const resizeObserver = new ResizeObserver(() => {
        updateButtons()
        scrollActiveIntoView()
    })

    resizeObserver.observe(thumbnails)

    const mediaQuery = globalThis.matchMedia('(min-width: 640px)')
    const onBreakpointChange = () => {
        updateButtons()
        scrollActiveIntoView()
    }

    mediaQuery.addEventListener('change', onBreakpointChange)

    updateButtons()

    return () => {
        mediaQuery.removeEventListener('change', onBreakpointChange)
        prevArrows.forEach((arrow) => {
            arrow.querySelector('button')?.removeEventListener('click', onPrevClick)
        })
        nextArrows.forEach((arrow) => {
            arrow.querySelector('button')?.removeEventListener('click', onNextClick)
        })
        resizeObserver.disconnect()
        dotObserver.disconnect()
    }
}

function ThumbnailSlider({ rootId }: Props) {
    useEffect(() => setup({ rootId }), [rootId])

    return <div className='hidden' data-thumbnail-slider-js />
}

export default ThumbnailSlider
